import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, CirclePlus } from 'lucide-react';

import { useQuery } from '@/helpers/useQuery';
import { mutateListOnDelete } from '@/helpers/swr';

import Page from '@/components/layout/Page';
import PageLoading from '@/components/layout/PageLoading';
import BackButton from '@/components/common/BackButton';
import HorizontalLogo from '@/components/common/HorizontalLogo';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription
} from '@/components/ui/empty';
import Error from '@/components/common/Error';
import { Button } from '@/components/ui/button';

import OfferingCard from '@/app/offering/ViewOfferingCard';

import { useCoachActivity } from '@/data/coachActivity';
import { useOfferings, useDeleteOffering } from '@/data/offering';
import { useApplication } from '@/data/application';

const ListOfferingsPage = () => {
  const navigate = useNavigate();
  const coachActivityId = useQuery('coachActivityId');
  const [apiError, setApiError] = useState('');
  const coachActivity = useCoachActivity(coachActivityId);
  const offerings = useOfferings({
    coachActivityId: coachActivityId
  });
  const deleteOffering = useDeleteOffering();
  const applicationStructure = useApplication({
    coachActivityId: coachActivityId
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (coachActivity.isLoading && !coachActivity.data) {
    return <PageLoading />;
  }
  if (offerings.isLoading && !offerings.data) {
    return <PageLoading />;
  }
  if (applicationStructure.isLoading && !applicationStructure.data) {
    return <PageLoading />;
  }

  const addOfferingLink = `/offering/add?coachActivityId=${coachActivityId}&backTo=/offering?coachActivityId=${coachActivityId}`;

  const handleEditOffering = (offeringId) => {
    navigate(
      `/offering/${offeringId}?backTo=/offering?coachActivityId=${coachActivityId}`
    );
  };

  const handleDeleteOffering = async (offeringId) => {
    try {
      await deleteOffering(offeringId);
      await offerings.mutate(
        (curr) => mutateListOnDelete(curr, offeringId),
        false
      );
    } catch (error) {
      setApiError(error.message);
    }
  };

  const handleAddOffering = () => {
    navigate(addOfferingLink);
  };

  return (
    <Page>
      <BackButton to="/setting" />
      <div className="p-8">
        <HorizontalLogo />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Offerings</CardTitle>
          <span className="text-muted-foreground block">
            Manage your offerings for {coachActivity.data.activity.title}.
          </span>
        </CardHeader>
        <CardContent>
          {apiError && <Error error={apiError} />}
          {offerings.data.query.length === 0 && (
            <Empty className="mb-14">
              <EmptyIcon>
                <Search />
              </EmptyIcon>
              <EmptyTitle>No Offerings Found</EmptyTitle>
              <EmptyDescription>Try creating an offering</EmptyDescription>
              <div className="py-4">
                <Link to={addOfferingLink}>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <CirclePlus />
                    Create Offering
                  </Button>
                </Link>
              </div>
            </Empty>
          )}
          {offerings.data.query.length > 0 && (
            <>
              <div className="space-y-4">
                {offerings.data.query.map((offeringId) => {
                  const offering = offerings.data.content[offeringId];
                  return (
                    <OfferingCard
                      key={offering.id}
                      offering={offering}
                      onEdit={handleEditOffering}
                      onDelete={handleDeleteOffering}
                    />
                  );
                })}
              </div>
              <div className="flex justify-center py-8">
                <Button
                  className="gap-2"
                  variant="ghost"
                  onClick={handleAddOffering}
                >
                  <CirclePlus />
                  Add Offering
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      <div className="flex justify-center py-8"></div>
    </Page>
  );
};

export default ListOfferingsPage;
